import { ReorderSlideDocument, type ReorderSlideMutation } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { SlideFields } from "@/types/Slide";
import { useMutation } from "@apollo/client/react";

export function useReorderSlide() {
  const [reorderSlideMutation, { loading }] = useMutation(ReorderSlideDocument);

  const reorderSlide = (
    slide: SlideFields,
    lessonId: string,
    prevSortOrder: number | null,
    newSortOrder: number | null
  ) => {
    const optimisticSortOrder = newSortOrder ?? (prevSortOrder ?? 0) + 1000;

    return withToast(
      async () => {
        return reorderSlideMutation({
          variables: {
            slideId: slide.id,
            lessonId,
            prevSortOrder,
            newSortOrder,
          },
          // The server may renumber siblings too, but only the moved slide can be
          // predicted here; the real response replaces this layer with the full set.
          optimisticResponse: {
            reorderSlide: [
              {
                __typename: "Slide",
                ...slide,
                sortOrder: optimisticSortOrder,
              } as ReorderSlideMutation["reorderSlide"][number],
            ],
          },
        });
      },
      {
        loading: "Updating...",
        success: "Slide order updated!",
        error: "Failed to update slide order. Please try again.",
      },
      "reorderSlideToast"
    );
  };

  return { reorderSlide, loading };
}
